import { Button, Spinner, useDisclosure } from '@nextui-org/react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';

import {
  IconArrowRightWhite,
  IconCheckChip,
  IconCloseChip,
  IconIdleChip,
  IconNewChip,
  IconSearchChip,
} from '@/assets';
import { Buttons } from '@/components';
import ListBar, { CATEGORY } from '@/components/list-bar/ListBarProfile';
import { useRouter } from '@/provider/RouterProvider';
import type { IAssetProcessing } from '@/types/asset';
import { STATUS_PROCESSING } from '@/types/asset';

import ListCardProcessing from './ListCardProcessing';
import useProcessing from './useProcessing';

const DynamicUpdateArtworkDetail = dynamic(() => import('./MintAsset'));

const Processing: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { onClose, onOpenChange, isOpen } = useDisclosure();
  // const { isMobileView, isMobileHorizontalView } = useWindowSize();
  const [selectedHide, setSelectedHide] = useState<string>('unhide');
  // const [selectedMinted, setSelectedMinted] = useState([
  //   STATUS_PROCESSING.GenerateError,
  //   STATUS_PROCESSING.GeneratePending,
  //   STATUS_PROCESSING.New,
  //   STATUS_PROCESSING.Retry,
  //   STATUS_PROCESSING.Minting,
  //   STATUS_PROCESSING.MintFailed,
  // ]);

  const {
    processList,
    setProcessData,
    loading,
    setSearch,
    setSort,
    handleAfterMultiMint,
    totalItems,
  } = useProcessing(20, false, selectedHide === 'hide', [1]);

  const onViewArtwork = (id: string | number | null) => {
    if (!id) return;
    router.push(`/artwork/${id}`);
  };

  const buttonUseCaseRender = (item: IAssetProcessing) => (
    <Button
      onClick={() =>
        router.push(
          item.useCase ? `/template/${item.useCase.id}` : `${pathname}/template/${item.id}`,
        )
      }
      className="h-fit rounded-sm px-0 hover:!bg-inherit focus:!bg-inherit"
      variant="light"
    >
      <span className="flex items-center gap-1 text-[14px] font-semibold text-white">
        {item.useCase ? 'View template' : 'Set as template'} <IconArrowRightWhite />
      </span>
    </Button>
  );

  const statusProcess = {
    [STATUS_PROCESSING.Minting]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-blue-50 px-[16px] py-[10px] text-[15px] font-semibold text-blue-500 lg:px-[12px] lg:py-[6px] lg:text-[13px]">
          <Spinner
            classNames={{
              circle1: 'border-b-[#3C8FF8]',
              circle2: 'border-b-[#3C8FF8]',
              wrapper: 'w-5 h-5 lg:w-4 lg-h-4',
            }}
          />{' '}
          <span className="ml-2">Minting</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex justify-center">{buttonUseCaseRender(item)}</div>
      ),
    },
    [STATUS_PROCESSING.Minted]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-blue-50 px-[16px] py-[10px] text-[15px] font-semibold text-blue-500 lg:px-[12px] lg:py-[6px] lg:text-[13px]">
          <IconCheckChip /> <span className="ml-2">Minted</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex items-center xl:block xl:text-center sm:flex sm:items-center">
          <Buttons
            className="min-w-[128px] lg:h-[36px] lg:text-[13px]"
            typeBtn="cancel"
            onClick={() => onViewArtwork(item?.tokenId)}
          >
            View artwork
          </Buttons>
          <div className="xl:mt-2 sm:ml-1 sm:mt-0">{buttonUseCaseRender(item)}</div>
        </div>
      ),
    },
    [STATUS_PROCESSING.MintFailed]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-red-50 px-[16px] py-[10px] text-[15px] font-semibold text-red-600 lg:px-[12px] lg:py-[6px] lg:text-[13px]">
          <IconCloseChip /> <span className="ml-2">Mint failed</span>
        </div>
      ),
      action: () => <Buttons className="lg:h-[36px] lg:text-[13px]">Try again</Buttons>,
    },
    [STATUS_PROCESSING.ViewSimilarity]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-gray-50 px-2 py-0 text-[15px] font-semibold text-black lg:text-[13px]">
          <IconSearchChip /> <span className="ml-2">View similarity check report</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex items-center xl:block xl:text-center sm:flex sm:items-center">
          <Buttons
            className="min-w-[128px] lg:h-[36px] lg:text-[13px]"
            typeBtn="cancel"
            onClick={() => onViewArtwork(item?.tokenId)}
          >
            View artwork
          </Buttons>
          <div className="xl:mt-2 sm:ml-1 sm:mt-0">{buttonUseCaseRender(item)}</div>
        </div>
      ),
    },
    [STATUS_PROCESSING.SimilarityChecking]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-yellow-50 px-2 py-0 text-[15px] font-semibold text-yellow-600 lg:text-[13px]">
          <Spinner
            classNames={{
              circle1: 'border-b-[#E8A700]',
              circle2: 'border-b-[#E8A700]',
              wrapper: 'w-5 h-5 lg:w-4 lg-h-4',
            }}
          />{' '}
          <span className="ml-2">Similarity checking</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex items-center xl:block xl:text-center sm:flex sm:items-center">
          <Buttons
            className="min-w-[128px] lg:h-[36px] lg:text-[13px]"
            typeBtn="cancel"
            onClick={() => onViewArtwork(item?.tokenId)}
          >
            View artwork
          </Buttons>
          <div className="xl:mt-2 sm:ml-1 sm:mt-0">{buttonUseCaseRender(item)}</div>
        </div>
      ),
    },
    [STATUS_PROCESSING.New]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] px-2 py-0 text-[15px] font-semibold text-orange-500 lg:text-[13px]">
          <IconNewChip /> <span className="ml-2">New</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex justify-start">{buttonUseCaseRender(item)}</div>
      ),
    },
    [STATUS_PROCESSING.Retry]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-gray-50 px-2 py-0 text-[15px] font-semibold text-black lg:text-[13px]">
          <IconIdleChip /> <span className="ml-2">Retry</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex justify-start">{buttonUseCaseRender(item)}</div>
      ),
    },
    [STATUS_PROCESSING.GeneratePending]: {
      view: (
        <div className="inline-flex items-center px-2 py-1 lg:text-[13px]">
          <Spinner
            classNames={{
              circle1: 'border-b-[#FBBF24]',
              circle2: 'border-b-[#FBBF24]',
              wrapper: 'w-5 h-5 lg:w-4 lg-h-4',
            }}
          />
          <span className="ml-2 text-[15px] font-semibold text-yellow-500">Generating</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex justify-start">{buttonUseCaseRender(item)}</div>
      ),
    },
    [STATUS_PROCESSING.GenerateError]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] px-2 py-0 lg:text-[13px]">
          <IconCloseChip />{' '}
          <span className="ml-2 text-[15px] font-semibold text-red-700">Failed</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex justify-start">{buttonUseCaseRender(item)}</div>
      ),
    },
    [STATUS_PROCESSING.Transfering]: {
      view: (
        <div className="inline-flex items-center rounded-[100px] bg-gray-50 px-2 py-0 text-[15px] font-semibold text-black lg:px-[12px] lg:py-[6px] lg:text-[13px]">
          <span>Transferring</span>
        </div>
      ),
      action: (item: IAssetProcessing) => (
        <div className="flex justify-start">{buttonUseCaseRender(item)}</div>
      ),
    },
  };
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center sm:w-full">
        <div className="w-full">
          <ListBar
            title=""
            onChangeSort={setSort}
            onChangeSearch={setSearch}
            placeholderSearch="Search in Processing assets"
            isFilter
            setSelectedHide={setSelectedHide}
            // setSelectedMinted={setSelectedMinted}
            category={CATEGORY.processing}
          />
        </div>
        {/* <div className="ml-2 w-[100px] sm:w-[90px]">
          <Buttons onClick={onOpen} className="min-w-[100px] sm:min-w-[90px]">
            Mint
          </Buttons>
        </div> */}
      </div>
      <div className="mt-7">
        {/* <RenderWhen>
          <RenderWhen.If isTrue={isIpadProView || isIpadView}>
            <TableProcessingTablet
              data={processList}
              statusProcess={statusProcess}
              loading={loading}
              currentPage={currentPage}
              loadMore={loadMore}
              hasMore={hasMore}
            />
          </RenderWhen.If>
          <RenderWhen.If isTrue={!isMobileHorizontalView && !isMobileView}>
            
          </RenderWhen.If>
          <RenderWhen.If isTrue={isMobileView || isMobileHorizontalView}>
            <TableProcessingMobile
              data={processList}
              statusProcess={statusProcess}
              setProcessData={setProcessData}
              totalItems={totalItems}
            />
          </RenderWhen.If>
        </RenderWhen> */}

        <ListCardProcessing
          data={processList}
          statusProcess={statusProcess}
          loading={loading}
          totalItems={totalItems}
          setProcessData={setProcessData}
        />
      </div>
      {isOpen && (
        <DynamicUpdateArtworkDetail
          onOpenChange={onOpenChange}
          onClose={onClose}
          isOpen={isOpen}
          handleAfterMultiMint={handleAfterMultiMint}
        />
      )}
    </div>
  );
};

export default Processing;
